import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import Col from 'reactstrap/lib/Col';
import { get, compact, orderBy, without } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleDetailsModel, VehicleDetailsEntities, buildComparisonsPath } from 'client/data/models/mmy-details';
import { PageModel } from 'client/data/models/page';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { buildSegmentRatingsPath, TypeRankingsFeaturesModel } from 'client/data/models/type-rankings-features';
import { buildPopularStylesPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { NO_VEHICLE_GRAY_IMAGE_SEDAN } from 'site-modules/shared/constants/images';
import { getDefaultStockPhotoBySize, getStaticImageUrl, SOURCES } from 'client/utils/image-helpers';
import {
  getCoreUrl,
  getVehicleComparisonUrl,
  getSelectedVehicleComparisonUrl,
} from 'site-modules/shared/utils/core-link-constructor';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getBestSegmentRating } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { buildRankingsSubsegmentLink } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { getValidMakeModelVsMakeModelLink } from 'site-modules/shared/utils/vehicle-comparisons-utils';
import { ArrowLink } from 'site-modules/shared/components/arrow-link/arrow-link';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { Image } from 'site-modules/shared/components/image/image';
import { Link } from 'site-modules/shared/components/link/link';

import './vehicle-comparisons.scss';

const DISPLAY_CARDS_NUM = {
  RANKINGS: { true: 5, false: 7 },
  DEFAULT: 3,
};
const MAX_COMPARE_SIZE = 4;

export function VehicleComparisonsUI({
  vehicleComparisons = null,
  segmentRating = null,
  styles = null,
  sharedTrimName = null,
  isMobile = false,
}) {
  const currentStyle = (styles && styles.find(({ trim }) => trim === sharedTrimName)) || get(styles, '[0]');

  const [selectedStyles, setSelectedStyles] = useState(
    compact([
      get(segmentRating, 'baseStyleId') ||
        get(vehicleComparisons, 'comparisons[0].vehicles[0].style') ||
        get(currentStyle, 'id'),
    ])
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronize selected comparison after model data changes.
    setSelectedStyles([
      get(segmentRating, 'baseStyleId') ||
        get(vehicleComparisons, 'comparisons[0].vehicles[0].style') ||
        get(currentStyle, 'id'),
    ]);
  }, [segmentRating, vehicleComparisons, currentStyle]);

  const onCheckboxChange = useCallback(
    event => {
      const checkedStyle = event?.target?.value;

      setSelectedStyles(
        selectedStyles.includes(checkedStyle)
          ? without(selectedStyles, checkedStyle)
          : [...selectedStyles, checkedStyle].slice(0, MAX_COMPARE_SIZE)
      );
    },
    [selectedStyles]
  );

  if (!get(vehicleComparisons, 'comparisons') && !segmentRating && !styles?.length) {
    return null;
  }

  const currentVehicleRank = get(segmentRating, 'rank');
  const displayCardsNum = currentVehicleRank ? DISPLAY_CARDS_NUM.RANKINGS[isMobile] : DISPLAY_CARDS_NUM.DEFAULT;
  let comparedVehicles = [];
  if (currentVehicleRank) {
    const currentRankedVehicle = segmentRating.editorialSegment.segmentRatings.find(
      ({ rank }) => rank === currentVehicleRank
    );

    comparedVehicles = segmentRating.editorialSegment.segmentRatings.slice(0, displayCardsNum);
    comparedVehicles[Math.min(displayCardsNum - 1, currentVehicleRank - 1)] = currentRankedVehicle;
    comparedVehicles = comparedVehicles
      .filter(({ modelYear }) => modelYear)
      .map(({ baseStyleId, modelYear, rankedSubmodel, slugRankedSubmodel, rank, priceRange }) => ({
        make: modelYear.makeName,
        model: modelYear.modelName,
        submodel: rankedSubmodel,
        makeSlug: modelYear.makeSlug,
        modelSlug: modelYear.modelSlug,
        submodelSlug: slugRankedSubmodel,
        year: modelYear.year,
        style: baseStyleId,
        rank,
        minPrice: priceRange?.min,
      }));
  }

  const comparisons = get(vehicleComparisons, 'comparisons', []);
  const textComparisons = comparisons.filter(({ content }) => content);
  const currentVehicle = get(comparisons, '[0].vehicles[0]') ||
    comparedVehicles[Math.min(displayCardsNum - 1, currentVehicleRank - 1)] || {
      ...currentStyle,
      make: currentStyle.makeName,
      model: currentStyle.modelName,
      style: currentStyle.id,
    };
  currentVehicle.isCurrentVehicle = true;
  if (!comparedVehicles.length && get(comparisons, 'length')) {
    comparedVehicles = [currentVehicle, ...comparisons.map(({ vehicles }) => vehicles[1])]
      .map(vehicle => ({
        ...vehicle,
        makeSlug: makeNiceName(vehicle.make),
        modelSlug: makeNiceName(vehicle.model),
      }))
      .slice(0, displayCardsNum);
  }

  const hasComparisons = !!comparedVehicles.length;
  comparedVehicles = hasComparisons ? comparedVehicles : [currentVehicle];

  return (
    <div className="vehicle-comparisons" data-tracking-parent="vs_the_competition_link">
      {currentVehicleRank ? (
        <hgroup
          className={classnames('d-flex flex-wrap column-gap-0_75', {
            'mb-1': comparedVehicles.length > MAX_COMPARE_SIZE,
            'mb-1_5': comparedVehicles.length <= MAX_COMPARE_SIZE,
          })}
        >
          <h2 className="heading-3 text-start mb-0">
            Where the {currentVehicle.make} {currentVehicle.model} ranks:
          </h2>
          <p className="size-16 mb-0">
            <strong className="size-24">#{currentVehicleRank}</strong> in{' '}
            <Link
              className="text-underline"
              to={buildRankingsSubsegmentLink(
                segmentRating.editorialSegment.edmundsTypeCategory,
                segmentRating.editorialSegment.displayName
              )}
              data-tracking-id="compare_vehicle_ranking"
            >
              {segmentRating.editorialSegment.displayName}
            </Link>
          </p>
          {comparedVehicles?.length > MAX_COMPARE_SIZE && (
            <p className="size-16 w-100 mt-1 mb-0">Select up to {MAX_COMPARE_SIZE} cars below to compare.</p>
          )}
        </hgroup>
      ) : (
        <h2 className="heading-3 text-start mb-1_5">
          Compare the {currentVehicle.make} {currentVehicle.model}
        </h2>
      )}
      <ul className="row list-unstyled">
        {comparedVehicles.map(
          (
            { make, model, makeSlug, modelSlug, year, submodel, submodelSlug, style, rank, minPrice, isCurrentVehicle },
            index
          ) => {
            const LinkTag = isCurrentVehicle ? 'span' : Link;
            const linkProps = isCurrentVehicle
              ? {}
              : {
                  to: getCoreUrl({ makeSlug, modelSlug, ...(submodelSlug ? { year, submodelSlug } : {}) }),
                  'data-tracking-id': 'compare_core_link',
                };

            return (
              <Col tag="li" xs={6} md={3} key={`${makeSlug}-${modelSlug}-${style}`} className="mb-1_5">
                <Card
                  className={classnames('overflow-hidden h-100 rounded-12', {
                    selected: currentVehicleRank ? rank === currentVehicleRank : !index,
                  })}
                >
                  <AspectRatioContainer aspectRatio="3:2" className="mb-0_5">
                    <Image
                      errorImage={getStaticImageUrl(NO_VEHICLE_GRAY_IMAGE_SEDAN)}
                      imageId={getDefaultStockPhotoBySize({ make: makeSlug, model: modelSlug, year }, 300, 200)}
                      source={SOURCES.MEDIA}
                      alt=""
                    />
                  </AspectRatioContainer>
                  <div className={classnames('vehicle-name mb-0_5 px-0_75', { 'text-center': !currentVehicleRank })}>
                    <LinkTag
                      {...linkProps}
                      id={`comparisons-name-${style}`}
                      className="d-inline-block fw-bold size-16 text-blue-30"
                    >
                      {make}&nbsp;{model} {submodel}
                    </LinkTag>
                  </div>
                  {!!(minPrice || hasComparisons) && (
                    <div className="mt-auto">
                      {!!minPrice && (
                        <div className="small px-0_75 mb-0_75">
                          Starting at <span className="size-16 fw-bold">{getPriceStringV2(minPrice)}</span>
                        </div>
                      )}
                      {hasComparisons && (
                        <Fragment>
                          <hr className="mb-0 mt-0 w-100" />
                          <Checkbox
                            id={`comparison-${style}`}
                            labelText="Compare"
                            containerClassName="compare-checkbox d-flex align-items-center text-gray-darker medium rounded px-0_75 my-0_5"
                            customLabelClassName="rounded"
                            textLabelClassName="ps-1_5"
                            data-hidedelightedoninteraction
                            onChange={onCheckboxChange}
                            value={style}
                            aria-labelledby={classnames(`checkbox-comparison-${style}`, `comparisons-name-${style}`)}
                            checked={selectedStyles.includes(style)}
                            data-tracking-id="compare_checkbox"
                            data-tracking-value={`${make} ${model}`}
                          />
                          {!!rank && (
                            <div className="rank pos-a top-0 left-0 heading-4 bg-gray-extra-light px-0_5 py-0_25">
                              {rank}
                            </div>
                          )}
                        </Fragment>
                      )}
                    </div>
                  )}
                </Card>
              </Col>
            );
          }
        )}
        <Col tag="li" xs={6} md={3} className="mb-1_5">
          <Card
            tag={Link}
            to={getSelectedVehicleComparisonUrl(selectedStyles, comparedVehicles)}
            className="placeholder-card overflow-hidden h-100 rounded-12"
            data-tracking-id="compare_cars_any"
          >
            <AspectRatioContainer aspectRatio="3:2" className="mb-0_5">
              <div>
                <div className="plus-container bg-blue-90 mx-auto pos-r">
                  <i className="icon-plus3 text-blue-30 pos-a center-xy" aria-hidden />
                </div>
              </div>
            </AspectRatioContainer>
            <div className="mb-0_5 px-1_5 text-center text-blue-30 d-inline-block fw-bold size-16 text-underline px-0_5">
              Add any vehicle to compare
            </div>
          </Card>
        </Col>
      </ul>
      {hasComparisons && (
        <div className="compare-links d-flex flex-column gap-1 align-items-center align-items-md-start mb-2">
          <Button
            tag={Link}
            to={getSelectedVehicleComparisonUrl(selectedStyles, comparedVehicles)}
            className="compare-button size-16 rounded-8 py-0_25 px-1 text-transform-none"
            color="blue-50"
            disabled={!selectedStyles.length}
            data-tracking-id="compare_cars_custom"
          >
            Compare selected vehicles
          </Button>
        </div>
      )}
      {!!get(textComparisons, 'length') && (
        <div>
          {comparisons.map((comparison, index) => {
            const key = `comp-${index + 1}`;
            const { make, model, style } = comparison.vehicles[0];
            const makeModelVsMakeModelLink = getValidMakeModelVsMakeModelLink(
              comparison.vehicles[0],
              comparison.vehicles[1]
            );

            return (
              comparison.content && (
                <Fragment key={key}>
                  <h3 className="mb-0 align-self-center heading-5 mt-1_5 mb-0_5">
                    {make} {model} vs. {comparison.vehicles[1].make} {comparison.vehicles[1].model}
                  </h3>
                  <ContentFragment classes="mb-0_5 mb-md-1 size-16">{comparison.content}</ContentFragment>
                  <ArrowLink
                    to={makeModelVsMakeModelLink || getVehicleComparisonUrl([style, comparison.vehicles[1].style])}
                    color="blue-40"
                    data-tracking-id="compare_cars"
                    data-tracking-value={`compare ${model} vs ${comparison.vehicles[1].model}`}
                  >
                    Compare {make} {model} & {comparison.vehicles[1].make} {comparison.vehicles[1].model} features
                  </ArrowLink>
                </Fragment>
              )
            );
          })}
        </div>
      )}
    </div>
  );
}

VehicleComparisonsUI.propTypes = {
  vehicleComparisons: VehicleDetailsEntities.ComparisonsContent,
  segmentRating: TypeRankingsEntities.SegmentRating,
  sharedTrimName: PropTypes.string,
  styles: VehicleEntities.PopularStyles,
  isMobile: PropTypes.bool,
};

export const VehicleComparisons = connectToModel(VehicleComparisonsUI, {
  vehicleComparisons: bindToPath(
    ({ vehicle }) => buildComparisonsPath(getParamsFromVehicle(vehicle)),
    VehicleDetailsModel
  ),
  segmentRating: bindToPath(
    ({
      vehicle: {
        make: { slug: makeSlug },
        model: { slug: modelSlug },
        year,
      },
    }) => buildSegmentRatingsPath({ makeSlug, modelSlug, year }),
    TypeRankingsFeaturesModel,
    (
      segmentRatings,
      {
        vehicle: {
          submodels: { slug: submodelSlug },
        },
      }
    ) => getBestSegmentRating(segmentRatings, { submodelSlug })
  ),
  sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
  styles: bindToPath(
    ({ vehicle }) => buildPopularStylesPath(getParamsFromVehicle(vehicle)),
    VehicleModel,
    popularStyles => orderBy(popularStyles, 'price.baseMSRP')
  ),
});

VehicleComparisons.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
};
